import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db, COLLECTIONS, auth, School, upsertUsersRootProfile } from '../../utils/firebase';
import { doc, getDocs, collection, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import Layout from '../../components/layout/Layout';
import DropdownButton from '../../components/ui/DropdownButton';
import Button from '../../components/ui/Button';
import HideIcon from '../../components/icons/HideIcon';
import ShowIcon from '../../components/icons/ShowIcon';
import DownIcon from '../../components/icons/DownIcon';
import GoogleIcon from '../../components/icons/GoogleIcon';
import MicrosoftIcon from '../../components/icons/MicrosoftIcon';
import CheckboxIcon from '../../components/icons/CheckboxIcon';
import { SCHOOL_REGISTRATION_FORM_URL } from '../../utils/externalLinks';

const pxToRem = (value: number) => `${value / 16}rem`;
const CONSENT_CHECKBOX_SIZE = 20;

interface ConsentCheckboxRowProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  children: React.ReactNode;
}

const ConsentCheckboxRow: React.FC<ConsentCheckboxRowProps> = ({
  checked,
  onChange,
  disabled = false,
  children,
}) => (
  <label
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: pxToRem(8),
      cursor: disabled ? 'default' : 'pointer',
      fontSize: pxToRem(14),
      fontFamily: 'Pretendard, sans-serif',
      color: 'var(--Color-text-normal, #161616)',
      lineHeight: pxToRem(21),
    }}
  >
    <span
      style={{
        position: 'relative',
        flexShrink: 0,
        width: pxToRem(CONSENT_CHECKBOX_SIZE),
        height: pxToRem(CONSENT_CHECKBOX_SIZE),
        marginTop: pxToRem(1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          margin: 0,
          opacity: 0,
          cursor: disabled ? 'default' : 'pointer',
        }}
      />
      <CheckboxIcon
        width={CONSENT_CHECKBOX_SIZE}
        height={CONSENT_CHECKBOX_SIZE}
        checked={checked}
        color="#989898"
        checkedColor="var(--Color-fill-brand-color, #FF6F0F)"
        style={{ pointerEvents: 'none' }}
      />
    </span>
    <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
  </label>
);

interface LocationState {
  email?: string;
  name?: string;
  displayName?: string;
  isSocialLogin?: boolean;
  isSocialSignup?: boolean;
  loginType?: 'google' | 'microsoft';
  uid?: string;
  from?: { pathname: string };
}

const assistiveColor = '#989898';

const TeacherRegister: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, loginWithGoogle, loginWithMicrosoft } = useAuth();
  const state = location.state as LocationState;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: state?.name || state?.displayName || '',
    email: state?.email || '',
    password: '',
    confirmPassword: '',
    schoolId: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [loadingSchools, setLoadingSchools] = useState(true);
  // 개인정보 수집 동의 및 만 14세 미만 보호자 동의 (학교운영위원회 심의·법령 준수)
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeGuardian, setAgreeGuardian] = useState(false);

  // 학교 목록 가져오기
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setLoadingSchools(true);
        const schoolsRef = collection(db, 'schools');
        const schoolsSnapshot = await getDocs(schoolsRef);
        const schoolsList = schoolsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as School[];
        
        schoolsList.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
        setSchools(schoolsList);
      } catch (error) {
        console.error('학교 목록 가져오기 오류:', error);
        setError('학교 목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoadingSchools(false);
      }
    };
    
    fetchSchools();
  }, []);

  // 소셜 로그인 후 currentUser나 state가 있을 때 이메일과 이름 자동 채우기
  useEffect(() => {
    // Firebase Auth의 실제 사용자 정보 가져오기
    const firebaseUser = auth.currentUser;
    const emailFromFirebase = firebaseUser?.email || '';
    const nameFromFirebase = firebaseUser?.displayName || '';
    
    // state나 currentUser에서 정보 가져오기
    const email = state?.email || currentUser?.email || emailFromFirebase || '';
    const displayName = state?.name || state?.displayName || currentUser?.name || nameFromFirebase || '';
    
    if (email || displayName) {
      setFormData(prev => {
        // 이미 값이 있으면 덮어쓰지 않음 (사용자가 수정한 경우)
        // 하지만 빈 문자열이면 업데이트
        const newEmail = prev.email || email;
        const newName = prev.name || displayName;
        
        // 값이 변경된 경우에만 업데이트
        if (newEmail !== prev.email || newName !== prev.name) {
          return {
            ...prev,
            email: newEmail,
            name: newName
          };
        }
        return prev;
      });
    }
  }, [currentUser, state?.email, state?.name, state?.displayName]);

  // 학교 선택 변경
  const handleSchoolChange = (schoolId: string) => {
    const school = schools.find(s => s.id === schoolId);
    setSelectedSchool(school || null);
    setFormData(prev => ({ ...prev, schoolId }));
  };

  // 폼 검증
  const isFormValid = () => {
    // 소셜 로그인인 경우 (state에 isSocialLogin이 있거나 currentUser가 있는 경우)
    const isSocialLogin = state?.isSocialLogin || state?.isSocialSignup || !!currentUser;
    
    // 학교 선택 확인 (selectedSchool 또는 formData.schoolId로 확인)
    const hasSchool = selectedSchool || formData.schoolId.trim() !== '';
    
    const consentValid = agreePrivacy && agreeGuardian;

    if (isSocialLogin) {
      // 소셜 로그인인 경우 비밀번호 검증 제외
      return (
        hasSchool &&
        formData.email.trim() !== '' &&
        formData.name.trim() !== '' &&
        consentValid &&
        !loading &&
        !loadingSchools
      );
    } else {
      // 일반 로그인인 경우 모든 필드 검증
      return (
        hasSchool &&
        formData.email.trim() !== '' &&
        formData.password.trim() !== '' &&
        formData.confirmPassword.trim() !== '' &&
        formData.password === formData.confirmPassword &&
        formData.name.trim() !== '' &&
        consentValid &&
        !loading &&
        !loadingSchools
      );
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'microsoft') => {
    setError('');
    setLoading(true);

    try {
      // 소셜 로그인 후 회원가입 페이지로 돌아오도록 설정
      const from = location.state?.from?.pathname || '/teacherregister';
      
      if (provider === 'google') {
        await loginWithGoogle('teacher');
      } else {
        await loginWithMicrosoft('teacher');
      }
    } catch (socialError: any) {
      console.error('소셜 로그인 오류:', socialError);

      if (socialError.code === 'auth/account-exists-with-different-credential') {
        setError('이미 다른 로그인 방식으로 등록된 계정입니다.\n이메일/비밀번호로 로그인하시거나, 다른 소셜 로그인 방식을 시도해주세요.');
      } else if (socialError.code === 'auth/popup-closed-by-user') {
        setError('로그인 창이 닫혔습니다. 다시 시도해주세요.');
      } else if (socialError.code === 'auth/cancelled-popup-request') {
        setError('로그인이 취소되었습니다. 다시 시도해주세요.');
      } else if (socialError.message === 'SOCIAL_SIGNUP_REQUIRED') {
        // 소셜 로그인 성공 후 추가 정보 입력을 위해 현재 페이지에 상태 전달
        navigate('/teacherregister', { 
          state: { 
            isSocialSignup: true, 
            provider,
            from: location.state?.from
          } 
        });
      } else {
        setError(socialError.message || '소셜 로그인에 실패했습니다. 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // 학교 선택 확인 (selectedSchool이 없으면 formData.schoolId로 찾기)
      let school = selectedSchool;
      if (!school && formData.schoolId) {
        school = schools.find(s => s.id === formData.schoolId) || null;
      }
      
      if (!school) {
        throw new Error('학교를 선택해주세요.');
      }

      const from = location.state?.from?.pathname || '/';

      // 소셜 로그인인 경우
      const isSocialLogin = state?.isSocialLogin || state?.isSocialSignup || !!currentUser;
      
      if (formData.password !== formData.confirmPassword && !isSocialLogin) {
        throw new Error('비밀번호가 일치하지 않습니다.');
      }
      if (isSocialLogin) {
        const firebaseUser = auth.currentUser;
        const userUid = firebaseUser?.uid || state?.uid || currentUser?.uid;

        if (!userUid) {
          if (state?.loginType === 'google') {
            await loginWithGoogle('teacher');
            return;
          } else if (state?.loginType === 'microsoft') {
            await loginWithMicrosoft('teacher');
            return;
          } else {
            throw new Error('로그인이 필요합니다. 구글 또는 Microsoft로 다시 로그인해 주세요.');
          }
        }

        if (!firebaseUser) {
          throw new Error('소셜 로그인 세션이 만료되었습니다. 구글 또는 Microsoft로 다시 로그인한 뒤 가입을 완료해 주세요.');
        }

        const { password: _password, confirmPassword: _confirmPassword, ...safeForm } = formData;
        const teacherData = {
          ...safeForm,
          uid: userUid,
          loginType: state?.loginType || 'social',
          role: 'teacher',
          schoolId: school.id,
          schoolCode: school.code,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: 'pending',
          grade: 0,
          class: 0,
          subject: '',
          stampClasses: [],
          classRoles: []
        };

        await setDoc(doc(db, 'teacher_requests', userUid), teacherData);

        await upsertUsersRootProfile({
          uid: userUid,
          email: formData.email || currentUser?.email || '',
          name: formData.name,
          role: 'teacher',
          schoolId: school.id,
          loginType: state?.loginType === 'google' || state?.loginType === 'microsoft' ? state.loginType : 'social',
        });

        if (from.startsWith('/class/') || from.startsWith('/admin/') || from.startsWith('/subject/')) {
          navigate('/class/main/ClassManagementHome', {
            state: {
              massage: '학교 관리자의 승인을 기다리는 중입니다. 승인 후 로그인이 가능합니다.'
            }
          });
        } else {
          navigate(from, {
            state: {
              massage: '학교 관리자의 승인을 기다리는 중입니다. 승인 후 로그인이 가능합니다.'
            }
          });
        }
      } else {
        // 일반 로그인인 경우
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );

        const { password: _password, confirmPassword: _confirmPassword, ...safeForm } = formData;
        const teacherData = {
          ...safeForm,
          uid: userCredential.user.uid,
          loginType: 'local',
          role: 'teacher',
          schoolId: school.id,
          schoolCode: school.code,
          createdAt: new Date(),
          updatedAt: new Date(),
          status: 'pending',
          grade: 0,
          class: 0,
          subject: '',
          stampClasses: [],
          classRoles: []
        };
        
        await setDoc(doc(db, 'teacher_requests', userCredential.user.uid), teacherData);

        await upsertUsersRootProfile({
          uid: userCredential.user.uid,
          email: formData.email,
          name: formData.name,
          role: 'teacher',
          schoolId: school.id,
          loginType: 'local',
        });

        if (from.startsWith('/class/') || from.startsWith('/admin/') || from.startsWith('/subject/')) {
          navigate('/class/main/ClassManagementHome', {
            state: {
              massage: '학교 관리자의 승인을 기다리는 중입니다. 승인 후 로그인이 가능합니다.'
            }
          });
        } else {
          navigate(from, {
            state: {
              massage: '학교 관리자의 승인을 기다리는 중입니다. 승인 후 로그인이 가능합니다.'
            }
          });
        }
      }
    } catch (err: unknown) {
      console.error('교사 등록 에러:', err);
      const firebaseErr = err as { code?: string; message?: string };
      if (firebaseErr.code === 'permission-denied') {
        setError('가입 요청 저장 권한이 없습니다. 소셜 로그인을 다시 시도하거나, 이메일 가입을 이용해 주세요.');
      } else {
        setError(firebaseErr.message || '교사 등록 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  const schoolOptions = schools.map(school => ({
    value: school.id,
    label: school.name
  }));

  // 소셜 로그인 여부 확인
  const isSocialLogin = state?.isSocialLogin || state?.isSocialSignup || !!currentUser;

  return (
    <Layout showNavbar requireAuth={false} navbarType="public">
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          paddingTop: pxToRem(80),
          paddingBottom: pxToRem(80),
          fontFamily: 'Pretendard, sans-serif'
        }}
      >
        <div
          style={{
            width: pxToRem(410),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: pxToRem(40)
          }}
        >
          {/* 제목 */}
          <div
            style={{
              alignSelf: 'stretch',
              textAlign: 'center',
              justifyContent: 'center',
              display: 'flex',
              flexDirection: 'column',
              color: 'black',
              fontSize: pxToRem(24),
              fontFamily: 'Pretendard, sans-serif',
              fontWeight: '500',
              lineHeight: pxToRem(36),
              wordWrap: 'break-word'
            }}
          >
            교사 회원가입
          </div>

          {/* 폼 영역 */}
          <form
            onSubmit={handleSubmit}
            style={{
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              gap: pxToRem(40)
            }}
          >
            <div
              style={{
                alignSelf: 'stretch',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: pxToRem(16),
                display: 'flex'
              }}
            >
              {/* "기본 정보를 입력해주세요" 텍스트 */}
              <div
                style={{
                  alignSelf: 'stretch',
                  height: pxToRem(24),
                  textAlign: 'center',
                  justifyContent: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  color: 'black',
                  fontSize: pxToRem(16),
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: '500',
                  lineHeight: pxToRem(24),
                  wordWrap: 'break-word'
                }}
              >
                기본 정보를 입력해주세요
              </div>

              {/* 회색 배경 박스 */}
              <div
                style={{
                  alignSelf: 'stretch',
                  height: pxToRem(334),
                  paddingLeft: pxToRem(28),
                  paddingRight: pxToRem(28),
                  paddingTop: pxToRem(8),
                  paddingBottom: pxToRem(8),
                  background: '#F8F8F8',
                  borderRadius: pxToRem(20),
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: pxToRem(10),
                  display: 'flex'
                }}
              >
                <div
                  style={{
                    width: pxToRem(354),
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: pxToRem(8),
                    display: 'flex'
                  }}
                >
                  {/* 학교 선택 드롭다운 */}
                  <div
                    style={{
                      alignSelf: 'stretch',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      gap: pxToRem(8),
                      display: 'flex'
                    }}
                  >
                    {schools.length > 0 && (
                      <DropdownButton
                        options={schoolOptions}
                        value={formData.schoolId}
                        onChange={handleSchoolChange}
                        placeholder="학교 선택"
                        width="100%"
                        height={pxToRem(48)}
                        size="large"
                        disabled={loadingSchools}
                      />
                    )}
                  </div>

                  {/* 학교 등록 요청 링크 */}
                  <div
                    style={{
                      alignSelf: 'stretch',
                      paddingTop: pxToRem(2),
                      paddingBottom: pxToRem(2),
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      display: 'inline-flex'
                    }}
                  >
                    <div
                      style={{
                        color: '#989898',
                        fontSize: pxToRem(12),
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '500',
                        lineHeight: pxToRem(18),
                        wordWrap: 'break-word'
                      }}
                    >
                      학교가 목록에 없으신가요?
                    </div>
                    <a
                      href={SCHOOL_REGISTRATION_FORM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textAlign: 'right',
                        color: '#FF6F0F',
                        fontSize: pxToRem(12),
                        fontFamily: 'Pretendard, sans-serif',
                        fontWeight: '500',
                        textDecoration: 'underline',
                        lineHeight: pxToRem(18),
                        wordWrap: 'break-word',
                        cursor: 'pointer'
                      }}
                    >
                      학교 등록 요청하기
                    </a>
                  </div>

                  {/* 입력 필드들 */}
                  <div
                    style={{
                      alignSelf: 'stretch',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      gap: pxToRem(8),
                      display: 'flex'
                    }}
                  >
                    {/* 이메일 입력 */}
                    <div
                      style={{
                        alignSelf: 'stretch',
                        height: pxToRem(48),
                        paddingLeft: pxToRem(16),
                        paddingRight: pxToRem(16),
                        paddingTop: pxToRem(12),
                        paddingBottom: pxToRem(12),
                        background: isSocialLogin ? '#F5F5F5' : 'white',
                        borderRadius: pxToRem(8),
                        outline: '1.2px solid #F0F0F0',
                        outlineOffset: pxToRem(-1.2),
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: pxToRem(8),
                        display: 'inline-flex',
                        opacity: isSocialLogin ? 0.6 : 1
                      }}
                    >
                      <input
                        type="email"
                        name="email"
                        required={!isSocialLogin}
                        disabled={isSocialLogin}
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="이메일을 입력해주세요"
                        style={{
                          flex: '1 1 0',
                          border: 'none',
                          outline: 'none',
                          color: formData.email ? '#161616' : '#989898',
                          fontSize: pxToRem(13),
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: '400',
                          lineHeight: pxToRem(19.5),
                          background: 'transparent',
                          cursor: isSocialLogin ? 'not-allowed' : 'text'
                        }}
                      />
                    </div>

                    {/* 비밀번호 입력 */}
                    <div
                      style={{
                        alignSelf: 'stretch',
                        height: pxToRem(48),
                        paddingLeft: pxToRem(16),
                        paddingRight: pxToRem(16),
                        paddingTop: pxToRem(12),
                        paddingBottom: pxToRem(12),
                        background: isSocialLogin ? '#F5F5F5' : 'white',
                        borderRadius: pxToRem(8),
                        outline: '1.2px solid #F0F0F0',
                        outlineOffset: pxToRem(-1.2),
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: pxToRem(8),
                        display: 'inline-flex',
                        opacity: isSocialLogin ? 0.6 : 1
                      }}
                    >
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        required={!isSocialLogin}
                        disabled={isSocialLogin}
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="비밀번호를 입력해주세요"
                        style={{
                          flex: '1 1 0',
                          border: 'none',
                          outline: 'none',
                          color: formData.password ? '#161616' : '#989898',
                          fontSize: pxToRem(13),
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: '400',
                          lineHeight: pxToRem(19.5),
                          background: 'transparent',
                          cursor: isSocialLogin ? 'not-allowed' : 'text'
                        }}
                      />
                      <div
                        onClick={() => !isSocialLogin && setShowPassword(!showPassword)}
                        style={{
                          cursor: isSocialLogin ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          opacity: isSocialLogin ? 0.5 : 1
                        }}
                      >
                        <HideIcon width={pxToRem(20)} height={pxToRem(20)} fill="#989898" />
                      </div>
                    </div>

                    {/* 비밀번호 확인 입력 */}
                    <div
                      style={{
                        alignSelf: 'stretch',
                        height: pxToRem(48),
                        paddingLeft: pxToRem(16),
                        paddingRight: pxToRem(16),
                        paddingTop: pxToRem(12),
                        paddingBottom: pxToRem(12),
                        background: isSocialLogin ? '#F5F5F5' : 'white',
                        borderRadius: pxToRem(8),
                        outline: '1.2px solid #F0F0F0',
                        outlineOffset: pxToRem(-1.2),
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: pxToRem(8),
                        display: 'inline-flex',
                        opacity: isSocialLogin ? 0.6 : 1
                      }}
                    >
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        required={!isSocialLogin}
                        disabled={isSocialLogin}
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder="비밀번호를 한번 더 입력해주세요"
                        style={{
                          flex: '1 1 0',
                          border: 'none',
                          outline: 'none',
                          color: formData.confirmPassword ? '#161616' : '#989898',
                          fontSize: pxToRem(13),
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: '400',
                          lineHeight: pxToRem(19.5),
                          background: 'transparent',
                          cursor: isSocialLogin ? 'not-allowed' : 'text'
                        }}
                      />
                      <div
                        onClick={() => !isSocialLogin && setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          cursor: isSocialLogin ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          opacity: isSocialLogin ? 0.5 : 1
                        }}
                      >
                        {showConfirmPassword ? (
                          <ShowIcon width={pxToRem(20)} height={pxToRem(20)} fill="#161616" />
                        ) : (
                          <HideIcon width={pxToRem(20)} height={pxToRem(20)} fill="#989898" />
                        )}
                      </div>
                    </div>

                    {/* 이름 입력 */}
                    <div
                      style={{
                        alignSelf: 'stretch',
                        height: pxToRem(48),
                        paddingLeft: pxToRem(16),
                        paddingRight: pxToRem(16),
                        paddingTop: pxToRem(12),
                        paddingBottom: pxToRem(12),
                        background: 'white',
                        borderRadius: pxToRem(8),
                        outline: '1.2px solid #F0F0F0',
                        outlineOffset: pxToRem(-1.2),
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: pxToRem(8),
                        display: 'inline-flex'
                      }}
                    >
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="이름을 입력해 주세요"
                        style={{
                          flex: '1 1 0',
                          border: 'none',
                          outline: 'none',
                          color: formData.name ? '#161616' : '#989898',
                          fontSize: pxToRem(13),
                          fontFamily: 'Pretendard, sans-serif',
                          fontWeight: '400',
                          lineHeight: pxToRem(19.5),
                          background: 'transparent'
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 개인정보 수집 동의 및 만 14세 미만 보호자 동의 (학교운영위원회 심의·법령 준수) */}
            <div
              style={{
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'column',
                gap: pxToRem(12)
              }}
            >
              <ConsentCheckboxRow
                checked={agreePrivacy}
                onChange={setAgreePrivacy}
                disabled={loading}
              >
                (필수) 개인정보 수집 및 이용에 동의합니다.
              </ConsentCheckboxRow>
              <ConsentCheckboxRow
                checked={agreeGuardian}
                onChange={setAgreeGuardian}
                disabled={loading}
              >
                (필수) (만 14세 미만인 경우) 학교 가정통신문 등을 통해 보호자 동의를 완료하였습니다.
              </ConsentCheckboxRow>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div
                style={{
                  alignSelf: 'stretch',
                  padding: `${pxToRem(12)} ${pxToRem(16)}`,
                  borderRadius: pxToRem(12),
                  background: 'rgba(255, 56, 56, 0.1)',
                  color: '#C33333',
                  fontSize: pxToRem(14),
                  textAlign: 'center',
                  boxSizing: 'border-box',
                  whiteSpace: 'pre-line'
                }}
              >
                {error}
              </div>
            )}

            {/* 등록 버튼 */}
            <Button
              type="submit"
              width="100%"
              height={pxToRem(48)}
              padding={`${pxToRem(16)} ${pxToRem(20)}`}
              variant="submit"
              size="large"
              disabled={!isFormValid() || loading}
            >
              {loading ? '등록 중...' : '등록'}
            </Button>
          </form>

          {/* 소셜 로그인 섹션 */}
          {!isSocialLogin && (
            <div
              style={{
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: pxToRem(12)
              }}
            >
              <div
                style={{
                  width: '100%',
                  textAlign: 'center',
                  color: assistiveColor,
                  fontSize: pxToRem(14),
                  fontFamily: 'Pretendard, sans-serif',
                  fontWeight: 500,
                  lineHeight: pxToRem(21)
                }}
              >
                다음 계정으로 계속하기
              </div>

              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: pxToRem(8)
                }}
              >
                <Button
                  variant="line"
                  size="large"
                  width="100%"
                  padding={`${pxToRem(16)} ${pxToRem(20)}`}
                  iconSize={18}
                  leftIcon={<GoogleIcon width={18} height={18} />}
                  disabled={loading}
                  onClick={() => handleSocialLogin('google')}
                >
                  Google로 계속하기
                </Button>

                <Button
                  variant="line"
                  size="large"
                  width="100%"
                  padding={`${pxToRem(16)} ${pxToRem(20)}`}
                  iconSize={18}
                  leftIcon={<MicrosoftIcon width={18} height={18} />}
                  disabled={loading}
                  onClick={() => handleSocialLogin('microsoft')}
                >
                  Microsoft로 계속하기
                </Button>
              </div>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
};

export default TeacherRegister;
